import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker assets when bundled — Leaflet otherwise fetches relative paths that 404.
const ICON = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function RecenterOnChange({ lat, lon, version }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lon != null) {
      map.setView([lat, lon], map.getZoom() || 11, { animate: true });
    }
    // version forces re-center even when lat/lon match a previous state
  }, [lat, lon, version, map]);
  return null;
}

function ClickHandler({ onPick }) {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
}

/**
 * Lightweight Leaflet map preview + click-to-pin.
 * Props:
 *   - latitude / longitude (number | null)
 *   - onChange(lat, lon) — called when user clicks the map or drags the pin
 *   - readOnly — disable click-to-pin and marker drag (view-only mode)
 */
export default function HiveLocationMap({ latitude, longitude, onChange, height = 220, readOnly = false }) {
  // Fall back to a neutral world view if no coords yet
  const center = useMemo(() => {
    if (latitude != null && longitude != null) return [latitude, longitude];
    return [20, 0];
  }, [latitude, longitude]);
  const zoom = latitude != null && longitude != null ? 11 : 2;
  const [version, setVersion] = useState(0);
  const markerRef = useRef(null);

  // Bump version when external lat/lon changes so we re-center even if the
  // user previously clicked elsewhere then typed an address.
  useEffect(() => { setVersion((v) => v + 1); }, [latitude, longitude]);

  return (
    <div className="rounded-lg overflow-hidden border border-stone-200" style={{ height }} data-testid="hive-location-map">
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={19}
        />
        <ClickHandler onPick={(lat, lon) => { if (!readOnly) onChange?.(lat, lon); }} />
        <RecenterOnChange lat={latitude} lon={longitude} version={version} />
        {latitude != null && longitude != null && (
          <Marker
            position={[latitude, longitude]}
            icon={ICON}
            draggable={!readOnly}
            eventHandlers={{
              dragend: () => {
                if (readOnly) return;
                const m = markerRef.current;
                if (m) {
                  const p = m.getLatLng();
                  onChange?.(p.lat, p.lng);
                }
              },
            }}
            ref={markerRef}
          />
        )}
      </MapContainer>
    </div>
  );
}
